import { Button } from "react-admin";
import { type EditCalendarEventDto, serverAPI } from "../../api/server.api";
import { OtherEvent } from "../../model/autovioCalendarEvents";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { OtherEventForm, type OtherEventFormValues } from "../../misc/calendar/OtherEventForm";
import { assertValid } from "../../backoffice.utils";
import { useOtherEventCategories } from "../../hooks/useOtherEventCategories";
import { durationToSubmit } from "../AddAppointmentDialog/createOtherEventDtos";

interface EditOtherEventFormProps {
  event: OtherEvent;
  closeEditMode: () => void;
}

export const EditOtherEventForm = (props: EditOtherEventFormProps) => {
  const { event, closeEditMode } = props;
  const otherEventCategories = useOtherEventCategories();

  const onSubmit = async (formValues: OtherEventFormValues) =>
    runSafely(
      async () => {
        const { categoryId, start, halfDayStartTime, end, duration, notes, vehicleId } = formValues;
        if (!categoryId) {
          throw new Error(`Invalid categoryId: ${categoryId}`);
        }
        const start_ = assertValid(start, "start");
        const submittedCategory = otherEventCategories.find((c) => c.id === categoryId);
        if (!submittedCategory) {
          throw new Error(`Category with id ${categoryId} not found.`);
        }
        const isVacationEventWithHalfDayDuration = submittedCategory.type === "vacation" && duration === "halfDay";
        if (isVacationEventWithHalfDayDuration && !halfDayStartTime) {
          throw new Error("halfDayStartTime is required for halfDay vacation events.");
        }
        const shouldSubmitDuration = submittedCategory.availableDurations.length > 0;
        const dto: EditCalendarEventDto = {
          type: "Other",
          categoryId,
          ...(shouldSubmitDuration
            ? {
                otherEventDuration: durationToSubmit(submittedCategory, duration, halfDayStartTime),
                start: start_
                  .set(
                    isVacationEventWithHalfDayDuration
                      ? { hour: halfDayStartTime!.hour, minute: halfDayStartTime!.minute }
                      : { hour: duration === "halfDayInTheAfternoon" ? 12 : 8, minute: 0 },
                  )
                  .toISO(),
              }
            : {
                start: start_.toISO(),
                end: assertValid(end, "end")
                  .set({
                    year: start_.year,
                    month: start_.month,
                    day: start_.day,
                  })
                  .toISO(),
              }),
          ...(vehicleId && { vehicleId }),
          notes: notes ?? "",
        };
        await serverAPI.editCalendarEvent({ eventId: event.id, dto });
      },
      { onSuccess: closeEditMode },
    );

  return (
    <OtherEventForm
      otherEvent={event}
      onSubmit={onSubmit}
      backButton={
        <Button
          startIcon={<NavigateBeforeIcon />}
          label="Zurück"
          variant="outlined"
          size="medium"
          onClick={closeEditMode}
        />
      }
    />
  );
};
